/**
 * Matchbooks: sixteen books of matches from the borough's pubs, clubs and caffs, dropped in alleys and
 * on back-street pavements. Each has a telephone number scrawled inside, and Lenny the Ear pays for
 * numbers. Find them on foot; Lenny pays for each one and adds a bonus at 4, 8, 12 and all 16 — and once
 * he has the lot, Des Halloran owes Lenny a favour (15 % off at the workshop).
 *
 * Spots are given as road-graph positions (two junctions, a fraction along, a side), so they follow the
 * layout; nothing here adds a collider or changes the layout. Unit-tested to sit clear of every collider.
 */
import type { V2 } from '../core/math';
import type { CityLayout } from '../world/types';
import { nodeId } from './roadLine';

export interface MatchbookSpot {
  id: string;
  /** The venue printed on the cover. */
  venue: string;
  /** Junction keys (world/lowBorough/data.ts) the street runs between, in that direction. */
  a: string;
  b: string;
  t: number;
  /** Left or right of travel from a to b. */
  side: 'L' | 'R';
  /** Cover colour (hex). */
  colour: number;
}

export const MATCHBOOK_SPOTS: readonly MatchbookSpot[] = [
  { id: 'mb-arch-row', venue: 'The Cinder Club', a: 'ar_n', b: 'ar_c', t: 0.35, side: 'L', colour: 0x7a1f1a },
  { id: 'mb-gin-alley', venue: 'The Coal Hole', a: 'ga_b', b: 'ga_c', t: 0.55, side: 'R', colour: 0x1e2a3a },
  { id: 'mb-shambles', venue: 'The Old Bell', a: 'bl_b', b: 'bl_c', t: 0.4, side: 'L', colour: 0x2f5233 },
  { id: 'mb-tanyard', venue: 'The Jolly Tanner', a: 't1_b', b: 't1_c', t: 0.3, side: 'R', colour: 0x8a5a1c },
  { id: 'mb-pilcher-ginnel', venue: 'The Lamplighter', a: 't2_n', b: 't2_b', t: 0.6, side: 'L', colour: 0xc9a227 },
  { id: 'mb-arch-lane', venue: 'Kessmouth Palais', a: 'cl_m', b: 'as_m', t: 0.45, side: 'R', colour: 0x6a2a5a },
  { id: 'mb-bonded-alley', venue: 'The Bargeman’s Rest', a: 'sa_bo', b: 'qs_bo', t: 0.7, side: 'L', colour: 0x24464a },
  { id: 'mb-quay-street', venue: 'Rotterdam Bar', a: 'qs_bo', b: 'qs_r', t: 0.6, side: 'R', colour: 0xd2642a },
  { id: 'mb-ropewalk', venue: 'Ma Lacey’s Pie & Mash', a: 'cl_r', b: 'pa_r', t: 0.5, side: 'L', colour: 0x3d6a8a },
  { id: 'mb-tarry-lane', venue: 'The Feathers', a: 'ws_d', b: 'ws_r', t: 0.5, side: 'R', colour: 0xe8e4d6 },
  { id: 'mb-slag-road', venue: 'Bert’s Caff', a: 'w_n', b: 'w_b', t: 0.5, side: 'L', colour: 0xa3201b },
  { id: 'mb-hob-lane', venue: 'The Grapes', a: 'hl_b', b: 'hl_c', t: 0.5, side: 'R', colour: 0x4a2440 },
  { id: 'mb-eastgate', venue: 'Club Calypso', a: 'e_ch', b: 'e_w', t: 0.5, side: 'L', colour: 0x2a8a6a },
  { id: 'mb-market-row', venue: 'The Crooked Anchor', a: 'as_m', b: 'pa_m', t: 0.5, side: 'L', colour: 0x1a1a1a },
  { id: 'mb-chapel-row', venue: 'The Gullet Arms', a: 'sq_e', b: 'e_ch', t: 0.5, side: 'L', colour: 0x7a5230 },
  { id: 'mb-lock-lane', venue: 'The Velvet Lamp', a: 'sq_se', b: 'lk_w', t: 0.5, side: 'R', colour: 0x5a1a3a },
];

/** On foot, within this many metres, and it's in your pocket. */
export const MATCHBOOK_REACH = 1.4;
/** The nearest unfound book within this many metres catches the light (a top-down camera sees ~25 m). */
export const MATCHBOOK_GLINT = 16;

/** Where the spot is in this layout (null if the layout lacks the street — stub layouts). */
export function matchbookPos(layout: CityLayout, spot: Pick<MatchbookSpot, 'a' | 'b' | 't' | 'side'>): V2 | null {
  const a = nodeId(layout, spot.a);
  const b = nodeId(layout, spot.b);
  if (a === null || b === null) return null;
  // Either direction: the street may be one-way the other way round; the spot is the same.
  const e = layout.roads.edges.find((q) => (q.from === a && q.to === b) || (q.from === b && q.to === a));
  if (!e) return null;
  const pa = layout.roads.nodes[a]!.pos;
  const pb = layout.roads.nodes[b]!.pos;
  const dx = pb.x - pa.x;
  const dz = pb.z - pa.z;
  const l = Math.hypot(dx, dz) || 1;
  // Left of travel a -> b is (dz, -dx) / l (heading convention, see world/roads.ts).
  const lx = dz / l;
  const lz = -dx / l;
  const off = e.alley || e.sidewalk <= 0 ? 1.55 : e.width / 2 + e.sidewalk * 0.55;
  const s = spot.side === 'L' ? 1 : -1;
  return { x: pa.x + dx * spot.t + lx * off * s, z: pa.z + dz * spot.t + lz * off * s };
}
